import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Search, Edit2, Trash2, Download, FileText, DollarSign, Clock, CheckCircle, AlertCircle, X, Eye, MapPin } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";
import PDFPreviewModal from "@/components/PDFPreviewModal";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-gray-500/20 text-gray-400", icon: FileText },
  sent: { label: "Sent", color: "bg-blue-500/20 text-blue-400", icon: Clock },
  paid: { label: "Paid", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
  overdue: { label: "Overdue", color: "bg-red-500/20 text-red-400", icon: AlertCircle },
  cancelled: { label: "Cancelled", color: "bg-gray-500/20 text-gray-500", icon: X },
};

const PAYMENT_TERMS = ["Due on Receipt", "Net 15", "Net 30", "Net 45", "Net 60", "Net 90"];

export default function InvoicesPage({ companyId, company }) {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Preview modal state
  const [previewInvoice, setPreviewInvoice] = useState(null);
  
  // Form state
  const [customerId, setCustomerId] = useState("");
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split("T")[0]);
  const [dueDate, setDueDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [taxRate, setTaxRate] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentInstructions, setPaymentInstructions] = useState("");
  const [lineItems, setLineItems] = useState([{ description: "", quantity: 1, unit_price: 0, taxable: true }]);
  
  // Bill To address state (can override customer's default)
  const [showBillTo, setShowBillTo] = useState(false);
  const [billToName, setBillToName] = useState("");
  const [billToStreet, setBillToStreet] = useState("");
  const [billToCity, setBillToCity] = useState("");
  const [billToState, setBillToState] = useState("");
  const [billToZip, setBillToZip] = useState("");
  
  // Ship To / Deliver To state
  const [showShipTo, setShowShipTo] = useState(false);
  const [shipToStreet, setShipToStreet] = useState("");
  const [shipToCity, setShipToCity] = useState("");
  const [shipToState, setShipToState] = useState("");
  const [shipToZip, setShipToZip] = useState("");
  
  // Legacy - keep for backwards compatibility
  const [showServiceLocation, setShowServiceLocation] = useState(false);
  const [serviceStreet, setServiceStreet] = useState("");
  const [serviceCity, setServiceCity] = useState("");
  const [serviceState, setServiceState] = useState("");
  const [serviceZip, setServiceZip] = useState("");

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append("status", filterStatus);
      
      const [invoicesRes, customersRes] = await Promise.all([
        api.get(`/companies/${companyId}/invoices?${params.toString()}`),
        api.get(`/companies/${companyId}/customers`)
      ]);
      
      setInvoices(invoicesRes.data.invoices || []);
      setStats(invoicesRes.data.stats || {});
      setCustomers(customersRes.data.customers || []);
    } catch (err) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [companyId, filterStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    // Set defaults from company
    if (company) {
      setTaxRate(String(company.default_tax_rate || 0));
      setPaymentTerms(company.default_payment_terms || "Due on Receipt");
      setPaymentInstructions(company.payment_instructions || "");
    }
  }, [company]);

  const calculateDueDate = (terms, fromDate) => {
    const date = new Date(fromDate);
    switch (terms) {
      case "Net 15": date.setDate(date.getDate() + 15); break;
      case "Net 30": date.setDate(date.getDate() + 30); break;
      case "Net 45": date.setDate(date.getDate() + 45); break;
      case "Net 60": date.setDate(date.getDate() + 60); break;
      case "Net 90": date.setDate(date.getDate() + 90); break;
      default: break; // Due on Receipt = same day
    }
    return date.toISOString().split("T")[0];
  };

  const resetForm = () => {
    setCustomerId("");
    setIssueDate(new Date().toISOString().split("T")[0]);
    setDueDate(calculateDueDate(company?.default_payment_terms || "Due on Receipt", new Date()));
    setPaymentTerms(company?.default_payment_terms || "Due on Receipt");
    setPoNumber("");
    setTaxRate(String(company?.default_tax_rate || 0));
    setNotes("");
    setPaymentInstructions(company?.payment_instructions || "");
    setLineItems([{ description: "", quantity: 1, unit_price: 0, taxable: true }]);
    setEditingInvoice(null);
    // Reset Bill To
    setShowBillTo(false);
    setBillToName("");
    setBillToStreet("");
    setBillToCity("");
    setBillToState("");
    setBillToZip("");
    // Reset Ship To
    setShowShipTo(false);
    setShipToStreet("");
    setShipToCity("");
    setShipToState("");
    setShipToZip("");
    // Reset legacy service location
    setShowServiceLocation(false);
    setServiceStreet("");
    setServiceCity("");
    setServiceState("");
    setServiceZip("");
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = async (invoice) => {
    try {
      const res = await api.get(`/companies/${companyId}/invoices/${invoice.invoice_id}`);
      const inv = res.data;
      setEditingInvoice(inv);
      setCustomerId(inv.customer_id);
      setIssueDate(inv.issue_date);
      setDueDate(inv.due_date);
      setPaymentTerms(inv.payment_terms);
      setPoNumber(inv.po_number || "");
      setTaxRate(String(inv.tax_rate));
      setNotes(inv.notes || "");
      setPaymentInstructions(inv.payment_instructions || "");
      setLineItems(inv.line_items?.length ? inv.line_items.map(item => ({
        ...item,
        taxable: item.taxable !== false // Default to true for backwards compatibility
      })) : [{ description: "", quantity: 1, unit_price: 0, taxable: true }]);
      
      // Load Bill To address if exists
      if (inv.bill_to && (inv.bill_to.name || inv.bill_to.street_address)) {
        setShowBillTo(true);
        setBillToName(inv.bill_to.name || "");
        setBillToStreet(inv.bill_to.street_address || "");
        setBillToCity(inv.bill_to.city || "");
        setBillToState(inv.bill_to.state || "");
        setBillToZip(inv.bill_to.zip_code || "");
      } else {
        setShowBillTo(false);
        setBillToName("");
        setBillToStreet("");
        setBillToCity("");
        setBillToState("");
        setBillToZip("");
      }
      
      // Load Ship To address if exists (check ship_to first, then legacy service_location)
      const shipToData = inv.ship_to || inv.service_location;
      if (shipToData && (shipToData.street_address || shipToData.city)) {
        setShowShipTo(true);
        setShipToStreet(shipToData.street_address || "");
        setShipToCity(shipToData.city || "");
        setShipToState(shipToData.state || "");
        setShipToZip(shipToData.zip_code || "");
      } else {
        setShowShipTo(false);
        setShipToStreet("");
        setShipToCity("");
        setShipToState("");
        setShipToZip("");
      }
      
      // Keep legacy state in sync
      setShowServiceLocation(false);
      setServiceStreet("");
      setServiceCity("");
      setServiceState("");
      setServiceZip("");
      
      setShowDialog(true);
    } catch (err) {
      toast.error("Failed to load invoice details");
    }
  };

  const handleSave = async () => {
    if (!customerId) {
      toast.error("Please select a customer");
      return;
    }
    if (!lineItems.some(item => item.description.trim())) {
      toast.error("Please add at least one line item");
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        customer_id: customerId,
        issue_date: issueDate,
        due_date: dueDate,
        payment_terms: paymentTerms,
        po_number: poNumber.trim(),
        tax_rate: parseFloat(taxRate) || 0,
        notes: notes.trim(),
        payment_instructions: paymentInstructions.trim(),
        line_items: lineItems.filter(item => item.description.trim()).map(item => ({
          description: item.description.trim(),
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          taxable: item.taxable !== false
        }))
      };
      
      // Add Bill To address if provided
      if (showBillTo && (billToName || billToStreet || billToCity)) {
        data.bill_to = {
          name: billToName.trim(),
          street_address: billToStreet.trim(),
          city: billToCity.trim(),
          state: billToState.trim(),
          zip_code: billToZip.trim()
        };
      }
      
      // Add Ship To address if provided
      if (showShipTo && (shipToStreet || shipToCity || shipToState || shipToZip)) {
        data.ship_to = {
          street_address: shipToStreet.trim(),
          city: shipToCity.trim(),
          state: shipToState.trim(),
          zip_code: shipToZip.trim()
        };
      }
      
      if (editingInvoice) {
        await api.put(`/companies/${companyId}/invoices/${editingInvoice.invoice_id}`, data);
        toast.success("Invoice updated");
      } else {
        await api.post(`/companies/${companyId}/invoices`, data);
        toast.success("Invoice created");
      }
      
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async (invoice) => {
    try {
      await api.post(`/companies/${companyId}/invoices/${invoice.invoice_id}/mark-paid`);
      toast.success("Invoice marked as paid");
      loadData();
    } catch (err) {
      toast.error("Failed to update invoice");
    }
  };

  const handleDelete = async (invoice) => {
    if (invoice.status !== "draft") {
      toast.error("Only draft invoices can be deleted");
      return;
    }
    if (!confirm(`Delete invoice ${invoice.invoice_number}?`)) return;
    
    try {
      await api.delete(`/companies/${companyId}/invoices/${invoice.invoice_id}`);
      toast.success("Invoice deleted");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete invoice");
    }
  };

  const handleDownloadPDF = async (invoice) => {
    // Open preview modal instead of direct download
    setPreviewInvoice(invoice);
  };

  const handleUpdateStatus = async (invoice, newStatus) => {
    try {
      await api.put(`/companies/${companyId}/invoices/${invoice.invoice_id}`, { status: newStatus });
      toast.success(`Invoice marked as ${newStatus}`);
      loadData();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: "", quantity: 1, unit_price: 0, taxable: true }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let taxableSubtotal = 0;
    
    lineItems.forEach(item => {
      const amount = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      subtotal += amount;
      if (item.taxable !== false) {
        taxableSubtotal += amount;
      }
    });
    
    const tax = taxableSubtotal * (parseFloat(taxRate) || 0) / 100;
    return { subtotal, taxableSubtotal, tax, total: subtotal + tax };
  };

  const totals = calculateTotals();

  if (!companyId) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Select a company to view invoices
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#d4c4b0]">Invoices</h1>
          <p className="text-[#8b9299] text-sm mt-1">Create and manage customer invoices</p>
        </div>
        <Button onClick={openNewDialog} className="btn-primary-gradient" disabled={customers.length === 0} data-testid="create-invoice-btn">
          <Plus className="w-4 h-4 mr-2" /> New Invoice
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Total Invoices</p>
                <p className="text-xl font-bold text-[#d4c4b0]">{stats.total_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Total Revenue</p>
                <p className="text-xl font-bold text-[#d4c4b0]">${(stats.total_amount || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Paid</p>
                <p className="text-xl font-bold text-[#d4c4b0]">${(stats.paid_amount || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Outstanding</p>
                <p className="text-xl font-bold text-[#d4c4b0]">${(stats.outstanding || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {["", "draft", "sent", "paid", "overdue"].map(status => (
          <Button
            key={status}
            variant={filterStatus === status ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterStatus(status)}
            className={filterStatus === status ? "btn-primary-gradient" : "border-border text-[#8b9299]"}
          >
            {status ? STATUS_CONFIG[status]?.label : "All"} {status && `(${stats[status] || 0})`}
          </Button>
        ))}
      </div>

      {/* Invoice List */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-6 h-6 border-2 border-[#e85d24] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : customers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-[#6b7280]">
              <FileText className="w-12 h-12 mb-3 opacity-50" />
              <p>Add customers first to create invoices</p>
              <Button variant="link" onClick={() => window.location.href = "/customers"} className="text-[#e85d24] mt-2">
                Go to Customers
              </Button>
            </div>
          ) : invoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-[#6b7280]">
              <FileText className="w-12 h-12 mb-3 opacity-50" />
              <p>No invoices found</p>
              <Button variant="link" onClick={openNewDialog} className="text-[#e85d24] mt-2">
                Create your first invoice
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-[#8b9299]">Invoice #</TableHead>
                    <TableHead className="text-[#8b9299]">Customer</TableHead>
                    <TableHead className="text-[#8b9299]">Date</TableHead>
                    <TableHead className="text-[#8b9299]">Due</TableHead>
                    <TableHead className="text-[#8b9299]">Status</TableHead>
                    <TableHead className="text-[#8b9299] text-right">Amount</TableHead>
                    <TableHead className="text-[#8b9299] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => {
                    const config = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.draft;
                    return (
                      <TableRow key={invoice.invoice_id} className="border-border hover:bg-secondary/50" data-testid={`invoice-row-${invoice.invoice_id}`}>
                        <TableCell className="text-[#d4c4b0] font-medium font-mono">{invoice.invoice_number}</TableCell>
                        <TableCell className="text-[#d4c4b0]">{invoice.customer_name}</TableCell>
                        <TableCell className="text-[#8b9299]">{invoice.issue_date}</TableCell>
                        <TableCell className="text-[#8b9299]">{invoice.due_date}</TableCell>
                        <TableCell>
                          <Badge className={config.color}>{config.label}</Badge>
                        </TableCell>
                        <TableCell className="text-[#d4c4b0] text-right font-mono">${invoice.total?.toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-primary hover:text-primary/80" onClick={() => setPreviewInvoice(invoice)} title="Preview PDF" data-testid={`preview-invoice-${invoice.invoice_id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8b9299] hover:text-[#d4c4b0]" onClick={() => handleDownloadPDF(invoice)} title="Download PDF">
                              <Download className="w-4 h-4" />
                            </Button>
                            {invoice.status === "draft" && (
                              <>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8b9299] hover:text-[#d4c4b0]" onClick={() => openEditDialog(invoice)} title="Edit">
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-400 hover:text-blue-300" onClick={() => handleUpdateStatus(invoice, "sent")} title="Mark as Sent">
                                  <Clock className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {(invoice.status === "sent" || invoice.status === "overdue") && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-400 hover:text-green-300" onClick={() => handleMarkPaid(invoice)} title="Mark as Paid">
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {invoice.status === "draft" && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDelete(invoice)} title="Delete">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">
              {editingInvoice ? `Edit Invoice ${editingInvoice.invoice_number}` : "Create Invoice"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Customer & Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-[#8b9299] text-sm">Customer *</Label>
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="invoice-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-48">
                    {customers.map(c => (
                      <SelectItem key={c.customer_id} value={c.customer_id} className="text-white">{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">Issue Date</Label>
                <Input type="date" value={issueDate} onChange={(e) => {
                  setIssueDate(e.target.value);
                  setDueDate(calculateDueDate(paymentTerms, e.target.value));
                }} className="mt-1.5 bg-secondary border-border text-white" data-testid="invoice-issue-date" />
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">Due Date</Label>
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1.5 bg-secondary border-border text-white" data-testid="invoice-due-date" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-[#8b9299] text-sm">Payment Terms</Label>
                <Select value={paymentTerms} onValueChange={(val) => {
                  setPaymentTerms(val);
                  setDueDate(calculateDueDate(val, issueDate));
                }}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {PAYMENT_TERMS.map(t => (
                      <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">PO Number (Optional)</Label>
                <Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="Customer's PO #" className="mt-1.5 bg-secondary border-border text-white" data-testid="invoice-po-number" />
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">Tax Rate (%)</Label>
                <Input type="number" step="0.01" min="0" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="mt-1.5 bg-secondary border-border text-white" data-testid="invoice-tax-rate" />
              </div>
            </div>

            <Separator className="bg-border/50" />
            
            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[#d4c4b0] font-medium">Line Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="border-border text-[#8b9299]">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {lineItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      {index === 0 && <Label className="text-[#6b7280] text-xs">Description</Label>}
                      <Input value={item.description} onChange={(e) => updateLineItem(index, "description", e.target.value)} placeholder="Service or product description" className="bg-secondary border-border text-white" data-testid={`line-item-desc-${index}`} />
                    </div>
                    <div className="col-span-1">
                      {index === 0 && <Label className="text-[#6b7280] text-xs">Qty</Label>}
                      <Input type="number" step="0.01" min="0" value={item.quantity} onChange={(e) => updateLineItem(index, "quantity", e.target.value)} className="bg-secondary border-border text-white" data-testid={`line-item-qty-${index}`} />
                    </div>
                    <div className="col-span-2">
                      {index === 0 && <Label className="text-[#6b7280] text-xs">Unit Price</Label>}
                      <Input type="number" step="0.01" min="0" value={item.unit_price} onChange={(e) => updateLineItem(index, "unit_price", e.target.value)} placeholder="0.00" className="bg-secondary border-border text-white" data-testid={`line-item-price-${index}`} />
                    </div>
                    <div className="col-span-2 flex items-center gap-2 pb-2">
                      {index === 0 && <Label className="text-[#6b7280] text-xs absolute -mt-6">Taxable</Label>}
                      <Checkbox 
                        id={`taxable-${index}`}
                        checked={item.taxable !== false}
                        onCheckedChange={(checked) => updateLineItem(index, "taxable", checked)}
                        className="border-[#8b9299]"
                        data-testid={`line-item-taxable-${index}`}
                      />
                      <Label htmlFor={`taxable-${index}`} className="text-[#8b9299] text-xs cursor-pointer">Tax</Label>
                    </div>
                    <div className="col-span-2 flex justify-end">
                      {lineItems.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => removeLineItem(index)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-1 text-sm">
                <div className="flex justify-between text-[#8b9299]">
                  <span>Subtotal:</span>
                  <span>${totals.subtotal.toFixed(2)}</span>
                </div>
                {totals.taxableSubtotal !== totals.subtotal && (
                  <div className="flex justify-between text-[#8b9299]">
                    <span>Taxable Amount:</span>
                    <span>${totals.taxableSubtotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#8b9299]">
                  <span>Tax ({taxRate || 0}%):</span>
                  <span>${totals.tax.toFixed(2)}</span>
                </div>
                <Separator className="bg-border/50 my-1" />
                <div className="flex justify-between text-[#d4c4b0] font-bold">
                  <span>Total:</span>
                  <span>${totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <Separator className="bg-border/50" />

            {/* Notes & Payment Instructions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#8b9299] text-sm">Notes (appears on invoice)</Label>
                <p className="text-[#6b7280] text-xs mt-0.5 mb-1">Use - for bullet points, **text** for bold</p>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Thank you for your business!" className="bg-secondary border-border text-white" rows={4} data-testid="invoice-notes" />
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">Payment Instructions</Label>
                <p className="text-[#6b7280] text-xs mt-0.5 mb-1">Use - for bullet points, **text** for bold</p>
                <Textarea value={paymentInstructions} onChange={(e) => setPaymentInstructions(e.target.value)} placeholder="Bank details, PayPal, etc.&#10;- Wire transfer: ...&#10;- PayPal: ..." className="bg-secondary border-border text-white" rows={4} data-testid="invoice-payment-instructions" />
              </div>
            </div>

            <Separator className="bg-border/50" />

            {/* Bill To Address (Override Customer's Default) */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Checkbox 
                  id="show-bill-to"
                  checked={showBillTo}
                  onCheckedChange={setShowBillTo}
                  className="border-[#8b9299]"
                />
                <Label htmlFor="show-bill-to" className="text-[#d4c4b0] font-medium cursor-pointer flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Custom Bill To Address (override customer's default)
                </Label>
              </div>
              
              {showBillTo && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-lg border border-border/50">
                  <div className="md:col-span-2">
                    <Label className="text-[#8b9299] text-sm">Company / Name</Label>
                    <Input 
                      value={billToName} 
                      onChange={(e) => setBillToName(e.target.value)} 
                      placeholder="Company or recipient name" 
                      className="mt-1.5 bg-secondary border-border text-white"
                      data-testid="bill-to-name"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[#8b9299] text-sm">Street Address</Label>
                    <Input 
                      value={billToStreet} 
                      onChange={(e) => setBillToStreet(e.target.value)} 
                      placeholder="123 Billing St" 
                      className="mt-1.5 bg-secondary border-border text-white"
                      data-testid="bill-to-street"
                    />
                  </div>
                  <div>
                    <Label className="text-[#8b9299] text-sm">City</Label>
                    <Input 
                      value={billToCity} 
                      onChange={(e) => setBillToCity(e.target.value)} 
                      placeholder="City" 
                      className="mt-1.5 bg-secondary border-border text-white"
                      data-testid="bill-to-city"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#8b9299] text-sm">State</Label>
                      <Input 
                        value={billToState} 
                        onChange={(e) => setBillToState(e.target.value)} 
                        placeholder="CA" 
                        className="mt-1.5 bg-secondary border-border text-white"
                        data-testid="bill-to-state"
                      />
                    </div>
                    <div>
                      <Label className="text-[#8b9299] text-sm">ZIP Code</Label>
                      <Input 
                        value={billToZip} 
                        onChange={(e) => setBillToZip(e.target.value)} 
                        placeholder="90210" 
                        className="mt-1.5 bg-secondary border-border text-white"
                        data-testid="bill-to-zip"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Ship To / Deliver To Address */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Checkbox 
                  id="show-ship-to"
                  checked={showShipTo}
                  onCheckedChange={setShowShipTo}
                  className="border-[#8b9299]"
                />
                <Label htmlFor="show-ship-to" className="text-[#d4c4b0] font-medium cursor-pointer flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Add Ship To / Deliver To Address
                </Label>
              </div>
              
              {showShipTo && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-lg border border-border/50">
                  <div className="md:col-span-2">
                    <Label className="text-[#8b9299] text-sm">Street Address</Label>
                    <Input 
                      value={shipToStreet} 
                      onChange={(e) => setShipToStreet(e.target.value)} 
                      placeholder="123 Delivery St" 
                      className="mt-1.5 bg-secondary border-border text-white"
                      data-testid="ship-to-street"
                    />
                  </div>
                  <div>
                    <Label className="text-[#8b9299] text-sm">City</Label>
                    <Input 
                      value={shipToCity} 
                      onChange={(e) => setShipToCity(e.target.value)} 
                      placeholder="City" 
                      className="mt-1.5 bg-secondary border-border text-white"
                      data-testid="ship-to-city"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#8b9299] text-sm">State</Label>
                      <Input 
                        value={shipToState} 
                        onChange={(e) => setShipToState(e.target.value)} 
                        placeholder="CA" 
                        className="mt-1.5 bg-secondary border-border text-white"
                        data-testid="ship-to-state"
                      />
                    </div>
                    <div>
                      <Label className="text-[#8b9299] text-sm">ZIP Code</Label>
                      <Input 
                        value={shipToZip} 
                        onChange={(e) => setShipToZip(e.target.value)} 
                        placeholder="90210" 
                        className="mt-1.5 bg-secondary border-border text-white"
                        data-testid="ship-to-zip"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-border text-white">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !customerId} className="btn-primary-gradient" data-testid="save-invoice-btn">
              {saving ? "Saving..." : (editingInvoice ? "Update Invoice" : "Create Invoice")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        open={!!previewInvoice}
        onClose={() => setPreviewInvoice(null)}
        title="Invoice Preview"
        documentType="invoice"
        companyId={companyId}
        documentId={previewInvoice?.invoice_id}
        documentNumber={previewInvoice?.invoice_number}
      />
    </div>
  );
}
